import type { CommonServerMessage, IceErrorCode, Reaction, ReactionEvent } from '@xox/shared'
import { type Effect, feedback } from '../lib/feedback'
import { type ConnectionStatus, RoomSocket } from '../lib/socket'

export const REACTION_TTL_MS = 2600
const MAX_VISIBLE_REACTIONS = 12

export interface RoomError {
  code: IceErrorCode
  message: string
  at: number
}

/** Fields every room socket store keeps, whatever the game. */
export interface CommonView {
  playerId: string | null
  /** serverTime - clientTime, smoothed from pongs. */
  clockOffset: number
  error: RoomError | null
  closedReason: 'expired' | 'restart' | 'closed' | null
  reactions: ReactionEvent[]
}

export const initialCommonView: CommonView = {
  playerId: null,
  clockOffset: 0,
  error: null,
  closedReason: null,
  reactions: [],
}

/**
 * Handles the protocol-independent frames. Returns null for game-specific ones so the caller can
 * take over. Pure.
 */
export function reduceCommon(
  view: CommonView,
  msg: { type: string },
  now = Date.now(),
): Partial<CommonView> | null {
  const m = msg as CommonServerMessage<IceErrorCode>
  switch (m.type) {
    case 'welcome':
      return { playerId: m.you.playerId, clockOffset: m.serverTime - now }
    case 'pong': {
      const rtt = now - m.t
      const sample = m.serverTime - (now - rtt / 2)
      return {
        clockOffset: view.clockOffset === 0 ? sample : view.clockOffset * 0.7 + sample * 0.3,
      }
    }
    case 'reaction': {
      const { type: _type, ...event } = m
      return { reactions: [...view.reactions, event].slice(-MAX_VISIBLE_REACTIONS) }
    }
    case 'error':
      return { error: { code: m.code, message: m.message, at: now } }
    case 'roomClosed':
      return { closedReason: m.reason }
    default:
      return null
  }
}

/**
 * Opens a room socket that runs every frame through the store's pure reducer and effects
 * deriver, plays the effects and expires reaction bubbles. Game stores add their own follow-ups
 * in `after`.
 */
export function openRoomSocket<V extends CommonView, M extends { type: string }>(
  code: string,
  path: string,
  io: {
    get: () => V
    set: (patch: Partial<V>) => void
    setStatus: (status: ConnectionStatus) => void
    reduce: (view: V, msg: M) => Partial<V>
    effects: (prev: V, next: V, msg: M) => Effect[]
    expireReaction: (id: string) => void
    after?: (msg: M, patch: Partial<V>) => void
  },
): RoomSocket {
  const socket = new RoomSocket(
    code,
    {
      onStatus: io.setStatus,
      onMessage: (raw) => {
        const msg = raw as unknown as M
        const prev = io.get()
        const patch = io.reduce(prev, msg)
        const next = { ...prev, ...patch }
        for (const effect of io.effects(prev, next, msg)) feedback(effect)
        io.set(patch)
        if (msg.type === 'reaction') {
          const { id } = msg as unknown as ReactionEvent
          setTimeout(() => io.expireReaction(id), REACTION_TTL_MS)
        }
        io.after?.(msg, patch)
      },
    },
    path,
  )
  socket.connect()
  return socket
}

function withoutReaction(reactions: ReactionEvent[], id: string) {
  return { reactions: reactions.filter((r) => r.id !== id) }
}

/** Store actions that are identical for every game; spread into the zustand store. */
export function commonActions<S extends CommonView>(
  socket: () => {
    send: (
      msg: { type: 'takeSeat' } | { type: 'leave' } | { type: 'react'; emoji: Reaction },
    ) => void
  } | null,
  set: (fn: Partial<S> | ((s: S) => Partial<S>)) => void,
) {
  return {
    takeSeat() {
      socket()?.send({ type: 'takeSeat' })
    },
    leave() {
      socket()?.send({ type: 'leave' })
    },
    react(emoji: Reaction) {
      socket()?.send({ type: 'react', emoji })
    },
    expireReaction(id: string) {
      set((s) => withoutReaction(s.reactions, id) as Partial<S>)
    },
    dismissError() {
      set({ error: null } as Partial<S>)
    },
  }
}
